import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import DocAiSummaryBoundary from './DocAiSummaryBoundary';
import {
  errorMessage,
  generateDocAiSummary,
  getDocAiSummary,
  updateDocAiSummaryItem,
} from './docAiSummaryApi';
import { formatInstantForViewer, getViewerTimezone } from '../../utils/dateTime';
import './DocAiSummaryModal.css';

// AI summary + obligations checklist for one document in Transaction →
// Documents. Layout follows the timeline's ChecklistModal (reference only —
// that component is not imported or changed). Generation runs in the
// background on the server; this modal polls until it finishes.

const POLL_MS = 3000;
const POLL_LIMIT_MS = 4 * 60 * 1000;
const REFRESH_MS = 15000;
const EMPTY_TEXT = 'No action items found in this document.';

const when = (iso, tz) => {
  const { dateLabel, timeLabel } = formatInstantForViewer(iso, tz);
  return dateLabel ? `${dateLabel}${timeLabel ? `, ${timeLabel}` : ''}` : '';
};

// Checklist items can be ticked, not edited (product decision: no Edit option).
const ItemRow = ({ item, readOnly, saving, tz, onToggle }) => {
  return (
    <div className={`docai-item ${item.done ? 'docai-item-done' : ''}`}>
      <div className="docai-item-main">
        <input
          id={`docai-item-${item.id}`}
          type="checkbox"
          className="docai-item-check"
          checked={!!item.done}
          disabled={readOnly || saving}
          onChange={() => onToggle(item)}
          aria-label={item.done ? 'Mark as not done' : 'Mark as done'}
        />
        <div className="docai-item-body">
          <div className="docai-item-text">{item.text}</div>
          <div className="docai-item-meta">
            <span><b>Due:</b> {item.dueDateText || 'Not stated'}</span>
            <span><b>Responsible:</b> {item.responsibleParty || 'Not stated'}</span>
            {item.sourcePage && <span>Page {item.sourcePage}</span>}
          </div>
          <div className="docai-item-trail">
            {item.doneAt && (
              <span>{item.done ? '✓ Done' : 'Marked not done'} by {item.doneByName || 'someone'} · {when(item.doneAt, tz)}</span>
            )}
            {/* Items edited before the Edit option was removed keep their history line. */}
            {item.edited && item.editedAt && (
              <span>Edited by {item.editedByName || 'someone'} · {when(item.editedAt, tz)}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const ModalInner = ({ transactionId, target, onClose }) => {
  const tz = getViewerTimezone();
  const [summaryId, setSummaryId] = useState(target.summaryId);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [itemError, setItemError] = useState('');
  const [slow, setSlow] = useState(false);
  const [savingItemId, setSavingItemId] = useState(null);
  const [staleDismissed, setStaleDismissed] = useState(false);
  const [viewVersion, setViewVersion] = useState(null); // null = current
  // Forms rows: the menu detected a newer version of the row (FR-11). Cleared once regenerated.
  const [clientStaleCleared, setClientStaleCleared] = useState(false);

  const alive = useRef(true);
  const pollTimer = useRef(null);
  const pollStarted = useRef(0);

  useEffect(() => () => {
    alive.current = false;
    clearTimeout(pollTimer.current);
  }, []);

  const poll = useCallback((id) => {
    clearTimeout(pollTimer.current);
    pollTimer.current = setTimeout(async () => {
      if (!alive.current) return;
      if (Date.now() - pollStarted.current > POLL_LIMIT_MS) {
        setSlow(true);
        return;
      }
      try {
        const next = await getDocAiSummary(transactionId, id, { checkStale: false });
        if (!alive.current) return;
        if (next.status === 'PENDING') {
          setData(next);
          poll(id);
        } else {
          // Finished — one last read that also checks for a newer signed copy.
          const final = await getDocAiSummary(transactionId, id, { checkStale: true });
          if (alive.current) setData(final);
        }
      } catch (err) {
        if (alive.current) poll(id); // transient — keep trying until the limit
      }
    }, POLL_MS);
  }, [transactionId]);

  const startGenerate = useCallback(async () => {
    setLoading(true);
    setError('');
    setSlow(false);
    setViewVersion(null);
    setStaleDismissed(false);
    try {
      const res = await generateDocAiSummary(transactionId, target.sourceKey);
      if (!alive.current) return;
      setClientStaleCleared(true);
      setSummaryId(res.summaryId);
      setData(res);
      if (res.status === 'PENDING') {
        pollStarted.current = Date.now();
        poll(res.summaryId);
      }
    } catch (err) {
      if (alive.current) setError(errorMessage(err, 'Could not start generating the summary. Please try again.'));
    } finally {
      if (alive.current) setLoading(false);
    }
  }, [transactionId, target, poll]);

  const load = useCallback(async (id, version) => {
    setLoading(true);
    setError('');
    try {
      const res = await getDocAiSummary(transactionId, id, { version, checkStale: !version });
      if (!alive.current) return;
      setData(res);
      if (res.status === 'PENDING') {
        pollStarted.current = Date.now();
        poll(id);
      }
    } catch (err) {
      if (alive.current) setError(errorMessage(err, 'Could not load the summary. Please try again.'));
    } finally {
      if (alive.current) setLoading(false);
    }
  }, [transactionId, poll]);

  // First load: generate, or open the existing summary.
  useEffect(() => {
    if (target.mode === 'generate' || !target.summaryId) startGenerate();
    else load(target.summaryId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the current version fresh so other parties' ticks and edits appear (FR-8).
  useEffect(() => {
    if (!summaryId || !data || data.status === 'PENDING' || viewVersion || savingItemId) return undefined;
    const timer = setInterval(async () => {
      try {
        const next = await getDocAiSummary(transactionId, summaryId, { checkStale: false });
        if (alive.current && next.status !== 'PENDING') {
          setData((prev) => (prev ? { ...next, stale: prev.stale, staleReason: prev.staleReason } : next));
        }
      } catch (err) { /* keep showing what we have */ }
    }, REFRESH_MS);
    return () => clearInterval(timer);
  }, [transactionId, summaryId, data, viewVersion, savingItemId]);

  const replaceItem = (updated) => setData((prev) => (prev ? {
    ...prev,
    items: prev.items.map((i) => (i.id === updated.id ? updated : i)),
  } : prev));

  const handleToggle = async (item) => {
    setItemError('');
    const before = item;
    replaceItem({ ...item, done: !item.done });
    setSavingItemId(item.id);
    try {
      const updated = await updateDocAiSummaryItem(transactionId, summaryId, item.id, {
        version: data.version,
        done: !item.done,
      });
      if (alive.current) replaceItem(updated);
    } catch (err) {
      if (!alive.current) return;
      replaceItem(before);
      setItemError(errorMessage(err, 'Could not save that change. Please try again.'));
    } finally {
      if (alive.current) setSavingItemId(null);
    }
  };


  const handleVersionChange = (e) => {
    const v = Number(e.target.value);
    const isCurrent = v === data.currentVersion;
    setViewVersion(isCurrent ? null : v);
    load(summaryId, isCurrent ? undefined : v);
  };

  const status = data?.status;
  const isPending = status === 'PENDING';
  const hasContent = (data?.version || 0) > 0;
  const docName = data?.displayName || target.sourceKey?.displayName || target.doc?.fileName || 'Document';
  // FR-11: decided by the server (content checksum / signed copy). Older Forms
  // summaries without a checksum still use the menu's row-state comparison.
  const isStale = !!data?.stale
    || (target.clientStale && !clientStaleCleared && hasContent && !data?.contentTracked);
  // FR-10: Regenerate only when generation failed or the summary is out of date.
  const canRegenerate = !isPending && !loading && !data?.readOnly && (status === 'FAILED' || (hasContent && isStale));

  return (
    <div className="docai-overlay" onClick={onClose}>
      <div className="docai-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Ria Summary">
        <div className="docai-header">
          <div className="docai-header-text">
            <div className="docai-title-row">
              <h2 className="docai-title">Ria Summary</h2>
              {/* <span className="docai-badge">AI-generated</span> */}
            </div>
            <p className="docai-docname" title={docName}>{docName}</p>
            {hasContent && (
              <p className="docai-meta">
                Generated {when(data.generatedAt, tz)}{data.generatedByName ? ` by ${data.generatedByName}` : ''}
                {' · '}Shared with primary parties
              </p>
            )}
          </div>
          <div className="docai-header-actions">
            {hasContent && data.versions?.length > 1 && (
              <select
                id="docai-version"
                className="docai-version-select"
                value={data.version}
                onChange={handleVersionChange}
                disabled={loading || isPending}
                aria-label="Version"
              >
                {data.versions.map((v) => (
                  <option key={v.version} value={v.version}>
                    Version {v.version}{v.version === data.currentVersion ? ' (current)' : ''}
                  </option>
                ))}
              </select>
            )}
            {canRegenerate && (
              <button type="button" className="docai-btn-secondary" onClick={startGenerate}>
                Regenerate
              </button>
            )}
            <button className="docai-close" onClick={onClose} aria-label="Close" type="button">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>

        <div className="docai-body">
          {(loading && !data) && (
            <div className="docai-loading"><span className="docai-spinner" aria-hidden="true" />Loading…</div>
          )}

          {error && <div className="docai-error">{error}</div>}

          {isPending && (
            <div className="docai-loading">
              <span className="docai-spinner" aria-hidden="true" />
              {slow
                ? 'This is taking longer than usual. You can close this window and check back later.'
                : 'Reading the document and generating your summary…'}
            </div>
          )}

          {!isPending && status === 'FAILED' && (
            <div className="docai-error">
              {data.failureReason || 'Could not generate a summary from this document.'}
              <button type="button" className="docai-inline-retry" onClick={startGenerate}>Try again</button>
            </div>
          )}

          {data?.readOnly && (
            <div className="docai-note">You're viewing an older version. It's read-only.</div>
          )}

          {!isPending && isStale && !staleDismissed && !data.readOnly && (
            <div className="docai-stale">
              <span>Document updated, regenerate?</span>
              <span className="docai-stale-actions">
                <button type="button" className="docai-btn-primary" onClick={startGenerate}>Regenerate</button>
                <button type="button" className="docai-btn-secondary" onClick={() => setStaleDismissed(true)}>Dismiss</button>
              </span>
            </div>
          )}

          {hasContent && !isPending && (
            <>
              <div className="docai-disclaimer">
                Generated summary based only on this document. It may be incomplete or inaccurate — refer to the full document for complete details.
              </div>
              {data.signedCopyUnavailable && (
                <div className="docai-note">Summarised from the unsigned original. The signed copy couldn't be retrieved.</div>
              )}

              <section className="docai-section">
                <h3 className="docai-section-title">Summary</h3>
                {data.overview && <p className="docai-overview">{data.overview}</p>}
                {data.keyPoints?.length > 0 && (
                  <ul className="docai-keypoints">
                    {data.keyPoints.map((p, i) => <li key={i}>{p}</li>)}
                  </ul>
                )}
                {data.unreadableSections?.length > 0 && (
                  <div className="docai-unreadable">
                    <div className="docai-unreadable-title">Sections that couldn't be read</div>
                    <ul>
                      {data.unreadableSections.map((u, i) => (
                        <li key={i}>
                          {u.section}{u.page ? ` (page ${u.page})` : ''}{u.reason ? ` — ${u.reason}` : ''}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </section>

              <section className="docai-section">
                <h3 className="docai-section-title">Checklist</h3>
                {itemError && <div className="docai-error docai-error-small">{itemError}</div>}
                {(data.items || []).length === 0 ? (
                  <div className="docai-empty">{EMPTY_TEXT}</div>
                ) : (
                  data.items.map((item) => (
                    <ItemRow
                      key={item.id}
                      item={item}
                      tz={tz}
                      readOnly={data.readOnly}
                      saving={savingItemId === item.id}
                      onToggle={handleToggle}
                    />
                  ))
                )}
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Renders the modal for the hook's current target, isolated by an error
// boundary and portalled to <body> so it can't disturb the Documents layout.
const DocAiSummaryModal = ({ docAi }) => {
  if (!docAi?.target || !docAi.transactionId) return null;
  return createPortal(
    <DocAiSummaryBoundary>
      <ModalInner
        key={docAi.target.sourceKey.key}
        transactionId={docAi.transactionId}
        target={docAi.target}
        onClose={docAi.close}
      />
    </DocAiSummaryBoundary>,
    document.body
  );
};

export default DocAiSummaryModal;
